import { FormEvent, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { OrganizationRoleV2 } from '../../../types/organizationTeam';
import { RoleChoice } from './TeamRolesPanel';
import { teamErrorText } from './teamErrorText';

interface AddMemberDialogProps {
  roles: OrganizationRoleV2[];
  onClose: () => void;
  onSubmit: (payload: { phone: string; roleId: string }) => Promise<unknown>;
  isSubmitting: boolean;
}

/** Owner adds an existing app user by phone, choosing their role from cards. */
export const AddMemberDialog = ({ roles, onClose, onSubmit, isSubmitting }: AddMemberDialogProps) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
  const [error, setError] = useState<string | undefined>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !roleId) return;
    setError(undefined);
    try {
      await onSubmit({ phone: phone.trim(), roleId });
      onClose();
    } catch (err) {
      setError(teamErrorText(err, t));
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('dashboard.team.members.addTitle')}</DialogTitle>
      <DialogContent>
        <form className="team-dialog-form" onSubmit={(e) => void submit(e)}>
          <label className="role-form-label" htmlFor="team-add-phone">
            {t('dashboard.team.members.phoneLabel')}
          </label>
          <input
            id="team-add-phone"
            type="tel"
            className="filter-search-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('dashboard.team.members.phonePlaceholder')}
            disabled={isSubmitting}
            autoFocus
          />
          <p className="role-form-hint">{t('dashboard.team.members.phoneHint')}</p>

          <fieldset className="team-role-choices">
            <legend className="role-form-label">{t('dashboard.team.members.pickRole')}</legend>
            {roles.map((r) => (
              <RoleChoice key={r.id} role={r} checked={roleId === r.id} onSelect={() => setRoleId(r.id)} name="team-add-role" />
            ))}
          </fieldset>

          {error && <p className="role-form-error" role="alert">{error}</p>}

          <div className="role-form-actions">
            <button type="button" className="secondary-action-btn" onClick={onClose} disabled={isSubmitting}>
              {t('dashboard.roles.form.cancel')}
            </button>
            <button type="submit" className="primary-action-btn" disabled={!phone.trim() || !roleId || isSubmitting}>
              {t('dashboard.team.members.addSubmit')}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
